import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { useGeoData } from '../../hooks/geoData/useGeoData';
import {
    getSiteLocaleFromPathname,
    isValidEmail,
    isValidPassword,
    scrollIntoViewIfNeeded,
    withSiteLocale,
} from '../../utils/helpers';
import {
    CountryOptionType,
    OptionType,
    RegisterPayload,
} from '../../utils/types';
import {
    Title,
    TextField,
    TextInput,
    Button,
    GenericError,
    Autocomplete,
    CountrySelect,
} from '../common';
import { LANGUAGES } from '../../utils/consts';
import { register } from '../../api/auth';
import { useTranslation } from '../../hooks/translations/TranslationProvider';
import EmailCode from '../EmailCode/EmailCode';
import useSeo from '../../hooks/useSeo';
import Checkbox from '../common/Checkbox/Checkbox';
import { GoogleAuthButton } from '../GoogleAuthButton/GoogleAuthButton';

import './register.css';
import UsernameField from './UsernameField';

const initialFormValues = {
    name: '',
    surname: '',
    username: '',
    email: '',
    password: '',
    countryCode: '',
    city: '',
    acceptTerms: false,
};

interface FormValues {
    name: string;
    surname: string;
    username: string;
    email: string;
    password: string;
    countryCode: string;
    city: string;
    acceptTerms: boolean;
}

interface Errors {
    name: string | null;
    surname: string | null;
    username: string | null;
    email: string | null;
    password: string | null;
    countryCode: string | null;
    city: string | null;
    acceptTerms: string | null;
}

const Register = () => {
    const auth = useAppSelector(state => state.auth);
    const navigate = useLocalizedNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const locale = getSiteLocaleFromPathname(location.pathname);

    useSeo({
        title: t('seo.register.title'),
        description: t('seo.register.description'),
        path: '/register',
        noIndex: true,
    });

    const {
        isLoading: isGeoDataLoading,
        error: geoDataError,
        citiesData,
        getCities,
    } = useGeoData();

    const [formValues, setFormValues] = useState<FormValues>({
        ...initialFormValues,
        countryCode: locale === 'pl' ? 'PL' : 'GB',
    });
    const [errors, setErrors] = useState<Errors>({
        ...initialFormValues,
        acceptTerms: null,
    });
    const [countryCode, setCountryCode] = useState(
        locale === 'pl' ? 'PL' : 'GB'
    );
    const [cityId, setCityId] = useState('');
    const [error, setError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showConfirmEmailForm, setShowConfirmEmailForm] = useState(false);

    useEffect(() => {
        if (auth.authData) {
            navigate('/home', { replace: true });
        }
    }, [auth, navigate]);

    const inputChangeHandler = (name: string, value: string) => {
        setFormValues({
            ...formValues,
            [name]: value,
        });
        setErrors({
            ...initialFormValues,
            acceptTerms: null,
        });
    };

    function usernameValidationHandler(hasError: boolean) {
        setErrors(prev => ({
            ...prev,
            username: hasError ? 'Username is required' : null,
        }));
    }

    const onSubmitHandler = async (event: React.FormEvent) => {
        event.preventDefault();

        let hasErrors = false;
        const formErrors = { ...errors };
        let key: keyof FormValues;
        let firstInputToFocus: HTMLElement | null = null;
        for (key in formValues) {
            if (!formValues[key]) {
                hasErrors = true;
                formErrors[key] = t('errors.requiredField');
                if (!firstInputToFocus) {
                    firstInputToFocus = document.getElementsByName(key)[0];
                }
            }
        }

        if (hasErrors) {
            setErrors({
                ...formErrors,
            });
            scrollIntoViewIfNeeded(firstInputToFocus);
            firstInputToFocus?.focus();
            return;
        }

        if (formValues.email && !isValidEmail(formValues.email)) {
            setErrors({ ...errors, email: 'Invalid email address' });
            return;
        }

        if (
            !formValues.password ||
            (formValues.password && !isValidPassword(formValues.password))
        ) {
            setErrors({
                ...errors,
                password: t('errors.wrongFormatPassword'),
            });
            return;
        }

        const payload: RegisterPayload = {
            ...formValues,
            language: locale.toUpperCase() as LANGUAGES,
            countryCode,
            city: cityId,
        };

        try {
            setIsLoading(true);
            await register(payload);

            setIsLoading(false);
            setShowConfirmEmailForm(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e: any) {
            if (e.response.status === 409) {
                setErrors({
                    ...formErrors,
                    email: t('errors.duplicateEmail'),
                });
                setIsLoading(false);
            }
            if (e.response.status === 400) {
                setErrors({
                    ...formErrors,
                    [e.response.data.errors[0].path]:
                        e.response.data.errors[0].msg,
                });
            }
            if (e.response.status === 500) {
                setError(true);
            }
            setIsLoading(false);
        }
    };

    const getCitiesHandler = async (initials: string) => {
        await getCities(countryCode, initials);
    };

    const generateOptions = useMemo(() => {
        const citiesOptions: OptionType[] = [];

        citiesData?.forEach(city => {
            citiesOptions.push({
                id: city.id,
                label: city.name,
            });
        });
        return { citiesOptions };
    }, [citiesData]);

    const onCountryChange = (fieldName: string, result: CountryOptionType) => {
        setCountryCode(result.id as string);

        setFormValues({
            ...formValues,
            city: '',
            [fieldName]: result.id,
        });

        setErrors({
            ...errors,
            countryCode: null,
            city: null,
        });
    };

    const onCityChange = (fieldName: string, result: OptionType) => {
        setCityId(result.id as string);
        setFormValues({
            ...formValues,
            [fieldName]: result.label,
        });

        setErrors({
            ...errors,
            countryCode: null,
            city: null,
        });
    };

    function onCheckboxChange(_: string, checked: boolean) {
        setFormValues({
            ...formValues,
            acceptTerms: checked,
        });
        setErrors({
            ...errors,
            acceptTerms: null,
        });
    }

    if (geoDataError || error) {
        return <GenericError error={error} />;
    }

    return (
        <main className="register-main page-container">
            {showConfirmEmailForm ? (
                <EmailCode email={formValues.email} />
            ) : (
                <>
                    <div className="register-title">
                        <Title text={t('register.title')} size="xxl" />
                        <TextField size="l" color="subtitle">
                            {t('register.subtitle')}
                        </TextField>
                    </div>
                    <GoogleAuthButton />
                    <form
                        onSubmit={onSubmitHandler}
                        className="register-form"
                        autoComplete="off"
                    >
                        <UsernameField
                            value={formValues.username}
                            onValueChange={value =>
                                setFormValues(prev => ({
                                    ...prev,
                                    username: value,
                                }))
                            }
                            onValidationChange={usernameValidationHandler}
                            formError={errors.username}
                        />
                        <div className="register-double-input">
                            <TextInput
                                label={t('register.name')}
                                name="name"
                                placeholder={t('register.name')}
                                onChange={inputChangeHandler}
                                value={formValues.name}
                                error={errors.name}
                            />
                            <TextInput
                                label={t('register.surname')}
                                name="surname"
                                placeholder={t('register.surname')}
                                onChange={inputChangeHandler}
                                value={formValues.surname}
                                error={errors.surname}
                            />
                        </div>
                        <TextInput
                            label="Email"
                            name="email"
                            placeholder="Email"
                            onChange={inputChangeHandler}
                            value={formValues.email}
                            error={errors.email}
                        />
                        <TextInput
                            label="Password"
                            name="password"
                            placeholder="Password"
                            onChange={inputChangeHandler}
                            value={formValues.password}
                            error={errors.password}
                            type="password"
                        />
                        <div className="register-country-city">
                            <TextField weight="bold">
                                {t('register.location')}
                            </TextField>
                            <div>
                                <CountrySelect
                                    name="countryCode"
                                    onSelect={onCountryChange}
                                    value={formValues.countryCode}
                                />

                                <Autocomplete
                                    name="city"
                                    startSearch={getCitiesHandler}
                                    isLoading={isGeoDataLoading}
                                    error={geoDataError}
                                    formError={errors.city}
                                    results={generateOptions.citiesOptions}
                                    handleOptionClick={onCityChange}
                                    placeholder={t('register.city')}
                                    initialValue={
                                        (formValues.city as string) || ''
                                    }
                                    iconName="bx-map"
                                    className="full-width"
                                />
                            </div>
                        </div>

                        <div className="register-accept-terms">
                            <Checkbox
                                name="acceptTerms"
                                isChecked={formValues.acceptTerms}
                                onChange={onCheckboxChange}
                                label={t('register.acceptTerms')}
                                error={errors.acceptTerms}
                            />
                        </div>
                        <Button
                            type="submit"
                            text={t('register.signUp')}
                            variant="green"
                            disabled={isLoading}
                        />
                    </form>
                    <div className="signin-link">
                        <TextField>{t('register.bottomText')}</TextField>
                        <Link to={withSiteLocale('/login', locale)}>
                            <TextField color="green">
                                {t('register.signIn')}
                            </TextField>
                        </Link>
                    </div>
                </>
            )}
        </main>
    );
};

export default Register;
